import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@nexus/ui";

import { ordersQueryOptions } from "@/lib/queries";

import { OrderCard } from "./OrderCard";

const SKELETON_ROWS = 2;

interface OrdersTabProps {
  tenantId: string;
  /** Order id to auto-expand, highlight, and scroll into view (deep link). */
  highlightOrderId?: string;
}

export function OrdersTab({ tenantId, highlightOrderId }: OrdersTabProps) {
  const { t } = useTranslation();
  const { data: orders = [], isLoading, isError } = useQuery(ordersQueryOptions(tenantId));

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("marketplace.orders.errorTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("marketplace.orders.empty")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {orders.map((order) => (
        <OrderCard
          key={order.id}
          order={order}
          tenantId={tenantId}
          highlight={order.id === highlightOrderId}
        />
      ))}
    </div>
  );
}
